import { Box, Collapse, Fade, Slide, Theme } from '@mui/material';
import { useEffect, useEffectEvent, useRef } from 'react';

import {
  playerActions,
  playerThunks,
  selectActiveChapterId,
  selectActiveEpisodeId,
  selectEpisodeCompliance,
  selectIsPlayerExpanded,
  useAppDispatch,
  useAppSelector,
  useFetchChapterQuery,
} from '@/store';

import { AudioVideoPlayer, MiniPlayer, VideoPlayer } from './components';

export const Player = () => {
  const dispatch = useAppDispatch();
  const trackPlayerRef = useRef<HTMLVideoElement>(null);
  const activeChapterId = useAppSelector(selectActiveChapterId);
  const activeEpisodeId = useAppSelector(selectActiveEpisodeId);
  const isExpanded = useAppSelector(selectIsPlayerExpanded);
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, activeEpisodeId),
  );

  const { isVideo } = useFetchChapterQuery(
    {
      chapterId: activeChapterId!,
      compliance,
    },
    {
      skip: !activeChapterId,
      selectFromResult: result => ({
        isVideo: result.data?.type === 'video',
      }),
    },
  );

  const handleKeydown = useEffectEvent((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isExpanded) {
      dispatch(playerActions.setExpanded(false));
    }
  });

  const player = (
    <AudioVideoPlayer isVideo={isVideo} trackPlayerRef={trackPlayerRef} />
  );

  useEffect(() => {
    dispatch(playerThunks.resumeLastSession());
    return () => {
      dispatch(playerActions.clearActiveChapter());
    };
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  return (
    <>
      <Box
        sx={styles.backdrop}
        onClick={() => dispatch(playerActions.setExpanded(false))}>
        <Fade in={isExpanded} timeout={250}>
          <Box
            sx={[
              styles.backdropExpanded,
              { pointerEvents: isExpanded ? 'auto' : 'none' },
            ]}
          />
        </Fade>
      </Box>
      <Box
        sx={[
          styles.container,
          {
            pointerEvents: isExpanded ? 'auto' : 'none',
          },
        ]}>
        <Slide direction="up" in={isExpanded} timeout={250}>
          <Box sx={styles.playerContainer}>
            <VideoPlayer player={player} trackPlayerRef={trackPlayerRef} />
          </Box>
        </Slide>
        <Collapse in={!!activeChapterId} timeout={250} unmountOnExit>
          <MiniPlayer />
        </Collapse>
      </Box>
    </>
  );
};

const styles = {
  backdrop: {
    pointerEvents: 'none',
    opacity: 0.8,
    height: '100%',
    left: 0,
    position: 'fixed',
    top: 0,
    width: '100%',
    zIndex: 'player',
  },
  backdropExpanded: {
    backgroundColor: 'blueBlack',
    opacity: 0.8,
    height: '100%',
    width: '100%',
  },
  container: (theme: Theme) => ({
    bottom: 0,
    position: 'fixed',
    width: '100%',
    zIndex: 'player',
    [theme.breakpoints.down('sm')]: {
      top: 0,
    },
  }),
  audioPlayer: {
    height: 0,
    width: 0,
  },
  playerContainer: (theme: Theme) => ({
    backgroundColor: 'primary.main',
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    bottom: 0,
    height: 'auto',
    padding: '50px 40px',
    position: 'relative',
    width: '100%',
    zIndex: -1, // render behind the parent container that is placed at index 1300
    [theme.breakpoints.down('sm')]: {
      padding: '30px 10px',
      height: 'calc(100% - 66px)',
    },
  }),
};
